import { PatientRepository } from "@/domain/repository/patient-repository";
import { DEFAULT_PHONE_COUNTRY_CODE, formatPhoneForServer, validatePhoneNumber } from "@/utils/phone-number-utils";
import { SATError, SATErrorType, SATResult } from "@sadtx/sat-error";
import { translate } from "@/utils/i18n";

export type UpdatePatientPayload = {
  name?: string;
  email?: string;
  phone?: string;
  countryCode?: string;
  detail?: {
    gender?: string;
    birthday?: string;
    memo?: string | null;
  };
};

export type UpdatePatientValidationErrors = Partial<Record<keyof UpdatePatientPayload, string>>;

export class UpdatePatientValidationError extends SATError {
  constructor(public readonly fieldErrors: UpdatePatientValidationErrors) {
    super(SATErrorType.ValidationError, translate("patient.validation.updateTitle"));
  }
}

export class UpdatePatientUsecase {
  constructor(private readonly patientRepo: PatientRepository) {}

  validate(payload: UpdatePatientPayload): UpdatePatientValidationErrors {
    const errors: UpdatePatientValidationErrors = {};
    const name = payload.name?.trim();
    const email = payload.email?.trim();
    const phone = payload.phone?.trim();
    const countryCode = payload.countryCode?.trim() || DEFAULT_PHONE_COUNTRY_CODE;
    const birthday = payload.detail?.birthday?.trim();

    if (payload.name !== undefined && !name) {
      errors.name = translate("patient.validation.nameRequired");
    } else if (name !== undefined && name.length <= 1) {
      errors.name = translate("patient.validation.nameMinLength");
    }

    if (payload.phone !== undefined || payload.countryCode !== undefined) {
      const phoneError = validatePhoneNumber(phone ?? "", countryCode);
      if (phoneError) errors.phone = phoneError;
    }

    if (email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      errors.email = translate("patient.validation.emailFormat");
    }

    if (birthday && !/^\d{4}-\d{2}-\d{2}$/.test(birthday)) {
      errors.detail = translate("patient.validation.dateFormatDash");
    }

    return errors;
  }

  async execute(
    patientUuid: string,
    payload: UpdatePatientPayload,
  ): Promise<SATResult<void, SATError>> {
    const countryCode = payload.countryCode?.trim() || DEFAULT_PHONE_COUNTRY_CODE;
    const detail = {
      ...(payload.detail?.gender !== undefined ? { gender: payload.detail.gender || "unknown" } : {}),
      ...(payload.detail?.birthday !== undefined ? { birthday: payload.detail.birthday.trim() } : {}),
      ...(payload.detail?.memo !== undefined ? { memo: payload.detail.memo?.trim() ?? "" } : {}),
    };
    const normalizedPayload: UpdatePatientPayload = {
      ...(payload.name !== undefined ? { name: payload.name.trim() } : {}),
      ...(payload.email !== undefined ? { email: payload.email.trim().toLowerCase() } : {}),
      ...(payload.phone !== undefined
        ? { phone: formatPhoneForServer(payload.phone, countryCode) }
        : {}),
      ...(payload.countryCode !== undefined ? { countryCode } : {}),
      ...(Object.keys(detail).length > 0 ? { detail } : {}),
    };
    const validationErrors = this.validate(normalizedPayload);
    if (Object.keys(validationErrors).length > 0) {
      return SATResult.failure(new UpdatePatientValidationError(validationErrors));
    }

    if (Object.keys(normalizedPayload).length === 0) {
      return SATResult.success(void 0);
    }

    return this.patientRepo.updatePatient(patientUuid, normalizedPayload);
  }
}
